import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { certifications, packagingOptions } from "@/mocks/products";
import { useProductsData } from "../hooks/useProductsData";
import { PackageX } from "lucide-react";

const eyebrow = "text-[14px] tracking-[0.25em] uppercase text-primary-600 mb-3";
const h2 =
  "font-heading text-3xl md:text-5xl leading-tight text-foreground-950 mb-5";

type Labels = { grade: string; packing: string; moisture: string };
type Labeled = { title: string; desc: string };

export function ProductsHero() {
  const { t } = useTranslation();
  return (
    <section className="relative h-[420px] md:h-[520px] w-full overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Extreme%20close%20up%20macro%20photograph%20of%20a%20tall%20heap%20of%20long%20grain%20polished%20white%20rice%20grains%20filling%20the%20frame%2C%20glistening%20smooth%20pearly%20translucent%20Vietnamese%20jasmine%20rice%20kernels%20neatly%20piled%20into%20a%20soft%20conical%20mound%20inside%20a%20shallow%20cream%20ceramic%20bowl%2C%20blurred%20folded%20soft%20linen%20fabric%20in%20the%20warm%20ivory%20background%2C%20soft%20diffused%20natural%20daylight%20with%20gentle%20highlights%20on%20every%20grain%2C%20shallow%20depth%20of%20field%2C%20clean%20bright%20airy%20food%20editorial%20photography&width=1920&height=700&seq=lam-products-hero&orientation=landscape&nocache=true"
        alt="Vietnamese export rice varieties including fragrant white rice, white rice, glutinous rice and broken rice"
        title="EverRice export rice products"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-background-50/85 via-background-50/50 to-transparent" />
      <div className="relative h-full flex items-center page-gutter">
        <div className="max-w-[780px] fade-up pt-16">
          <p className="font-heading text-lg tracking-[0.3em] text-foreground-700 mb-4">
            {t("productsPage.hero.eyebrow")}
          </p>
          <h1 className="font-heading text-4xl md:text-6xl !leading-[1.3] tracking-[0.03em] text-foreground-950 mb-6 whitespace-pre-line">
            {t("productsPage.hero.title")}
          </h1>
          <p className="text-sm md:text-base text-foreground-800 leading-relaxed">
            {t("productsPage.hero.desc")}
          </p>
        </div>
      </div>
    </section>
  );
}

export function ProductGrid() {
  const { t } = useTranslation();
  const labels = t("productsPage.grid.labels", {
    returnObjects: true,
  }) as unknown as Labels;

  // 🚀 Lấy dữ liệu danh mục & sản phẩm thực tế từ API theo ngôn ngữ hiện tại
  const { categories, products, isLoading } = useProductsData();
  const [active, setActive] = useState("all");

  // Tối ưu hóa việc lọc sản phẩm
  const shown = useMemo(() => {
    return active === "all"
      ? products
      : products.filter((p) => p.cat === active);
  }, [active, products]);

  return (
    <section className="bg-background-50 page-gutter py-16 md:py-24">
      {/* Nút lọc danh mục động lấy từ API */}
      <div className="flex flex-wrap gap-3 mb-12">
        <button
          type="button"
          onClick={() => setActive("all")}
          className={`px-5 py-2.5 rounded-full text-xs tracking-widest uppercase whitespace-nowrap cursor-pointer border transition-colors ${
            active === "all"
              ? "bg-primary-500 border-primary-500 text-background-50"
              : "bg-background-100 border-background-200 text-foreground-700 hover:border-primary-300"
          }`}
        >
          {t("productsPage.grid.all", "All")}
        </button>
        {categories.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setActive(c.id)}
            className={`px-5 py-2.5 rounded-full text-xs tracking-widest uppercase whitespace-nowrap cursor-pointer border transition-colors ${
              active === c.id
                ? "bg-primary-500 border-primary-500 text-background-50"
                : "bg-background-100 border-background-200 text-foreground-700 hover:border-primary-300"
            }`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="py-20 text-center text-sm text-foreground-600">
          Đang tải danh sách sản phẩm...
        </div>
      ) : shown.length === 0 ? (
        <div className="py-20 px-6 text-center bg-background-100/60 border border-dashed border-background-300 rounded-2xl flex flex-col items-center justify-center space-y-3">
          <div className="w-14 h-14 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center text-2xl shadow-sm">
            <PackageX size={28} />
          </div>
          <h3 className="font-heading text-lg text-foreground-900">
            Không tìm thấy sản phẩm
          </h3>
          <p className="text-xs text-foreground-600 max-w-sm leading-relaxed">
            Hiện tại danh mục này chưa có sản phẩm xuất khẩu nào cho ngôn ngữ
            này. Vui lòng lựa chọn danh mục khác.
          </p>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          data-product-shop
        >
          {shown.map((p) => (
            <article
              key={p.id}
              className="group bg-background-100 rounded-lg overflow-hidden border border-background-200 flex flex-col shadow-xs hover:shadow-md transition-shadow"
            >
              <div className="w-full h-52 overflow-hidden bg-background-200">
                <img
                  src={p.img}
                  alt={p.name}
                  title={`${p.name} Vietnamese rice`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5 flex flex-col flex-1">
                <span className="text-[10px] tracking-widest uppercase text-primary-600 mb-2">
                  {categories.find((c) => c.id === p.cat)?.name ||
                    p.categoryName}
                </span>
                <h3 className="font-heading text-xl mb-2">{p.name}</h3>
                <p className="text-xs text-foreground-600 leading-relaxed mb-4 flex-1">
                  {p.desc}
                </p>
                <dl className="text-[11px] text-foreground-700 space-y-1 border-t border-background-200 pt-4">
                  <div className="flex justify-between gap-3">
                    <dt className="text-foreground-500 uppercase tracking-wider">
                      {labels?.grade || "Grade"}
                    </dt>
                    <dd className="text-right">{p.grade}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-foreground-500 uppercase tracking-wider">
                      {labels?.packing || "Packing"}
                    </dt>
                    <dd className="text-right">{p.packaging}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-foreground-500 uppercase tracking-wider">
                      {labels?.moisture || "Moisture"}
                    </dt>
                    <dd className="text-right">{p.moisture}</dd>
                  </div>
                </dl>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

export function Certifications() {
  const { t } = useTranslation();
  const items = t("productsPage.certs.items", {
    returnObjects: true,
  }) as unknown as Labeled[];
  return (
    <section className="bg-primary-50 page-gutter py-16 md:py-24">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-10 items-center">
        <div>
          <p className={eyebrow}>{t("productsPage.certs.eyebrow")}</p>
          <h2 className={h2}>{t("productsPage.certs.title")}</h2>
          <p className="text-sm text-foreground-700 leading-relaxed">
            {t("productsPage.certs.desc")}
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.isArray(items) &&
            items.map((c, i) => (
              <div
                key={c.title || i}
                className={`rounded-lg p-6 text-center transition-transform duration-300 hover:-translate-y-1 ${
                  i === 1
                    ? "bg-gradient-to-br from-primary-500 via-primary-700 to-primary-900 text-background-50 shadow-lg"
                    : "bg-background-50 border border-background-200 shadow-md"
                }`}
              >
                <div
                  className={`w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4 ${
                    i === 1 ? "bg-background-50/20" : "bg-primary-100"
                  }`}
                >
                  <i
                    className={`${certifications[i]?.icon || "ri-award-line"} text-2xl ${
                      i === 1 ? "text-background-50" : "text-primary-600"
                    }`}
                  />
                </div>
                <h3 className="text-xs tracking-widest uppercase font-medium mb-2">
                  {c.title}
                </h3>
                <p
                  className={`text-xs leading-relaxed ${
                    i === 1 ? "text-background-100" : "opacity-80"
                  }`}
                >
                  {c.desc}
                </p>
              </div>
            ))}
        </div>
      </div>
    </section>
  );
}

export function Packaging() {
  const { t } = useTranslation();
  const items = t("productsPage.packaging.items", {
    returnObjects: true,
  }) as unknown as Labeled[];
  return (
    <section className="bg-background-100 page-gutter py-16 md:py-24">
      <div className="text-center mb-12">
        <p className={eyebrow}>{t("productsPage.packaging.eyebrow")}</p>
        <h2 className={`${h2} mb-0`}>{t("productsPage.packaging.title")}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {Array.isArray(items) &&
          items.map((o, i) => (
            <div
              key={o.title || i}
              className={`rounded-lg p-8 transition-transform duration-300 hover:-translate-y-1 ${
                i === 1
                  ? "bg-gradient-to-br from-secondary-400 via-secondary-500 to-secondary-700 text-background-50 shadow-lg"
                  : "bg-background-50 border border-background-200 shadow-md"
              }`}
            >
              <div className="w-14 h-14 flex items-center justify-center mb-5">
                <i
                  className={`${packagingOptions[i]?.icon || "ri-box-3-line"} text-3xl ${
                    i === 1 ? "text-background-50" : "text-primary-600"
                  }`}
                />
              </div>
              <h3 className="font-heading text-2xl mb-3">{o.title}</h3>
              <p
                className={`text-sm leading-relaxed ${
                  i === 1 ? "opacity-90" : "text-foreground-600"
                }`}
              >
                {o.desc}
              </p>
            </div>
          ))}
      </div>
      <div className="flex flex-col md:flex-row items-center justify-between gap-6 mt-14 rounded-lg bg-primary-100 px-6 md:px-12 py-8">
        <div>
          <h3 className="font-heading text-2xl md:text-3xl mb-2">
            {t("productsPage.packaging.box.title")}
          </h3>
          <p className="text-sm text-foreground-700">
            {t("productsPage.packaging.box.desc")}
          </p>
        </div>
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-xs tracking-widest uppercase whitespace-nowrap cursor-pointer"
        >
          {t("productsPage.packaging.box.cta")}{" "}
          <i className="ri-arrow-right-line" />
        </Link>
      </div>
    </section>
  );
}
